import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Grid,
  notification,
  List,
  Modal,
  Row,
  Space,
  Typography,
  InputNumber,
  Divider,
  FormInstance,
  Select,
} from "antd";
import Flex from "../../../components/Flex";
import { CoverWizardForm } from "../OrderContextMenu/CoverWizardModal/CoverWizardModal";
import ModalSaveCancelButtons from "../../../components/ModalSaveCancelButtons";
import { css } from "@emotion/css";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import WizardPageWrapper from "../OrderContextMenu/CoverWizardModal/components/WizardPageWrapper";
import {
  getHardwareItems,
  getPricedHardwareItems,
} from "../../../store/actions/hardwareCreators";
import { HardwareItemDto } from "../../../store/actions/dtos/HardwareItemDto";
import { PricedHardwareItemDto } from "../../../store/actions/dtos/PricedHardwareItemDto";
import {
  LineItem,
  PricedHardwareItemsRequestDto,
} from "../../../store/actions/dtos/PricedHardwareItemsRequestDto";
import { getUserFromStorage } from "../../../util/localStorage.util";
import { CreateHardwareOrderForm } from "./CreateHardwareOrderModal";
import { Skeleton } from "../../../components/Skeleton";
import { useIsDarkModeEnabled } from "../../../hooks/useIsDarkModeEnabled";

type AdditionalHardwarePageProps = {
  wizardPageNumberState: [number, React.Dispatch<React.SetStateAction<number>>];
  onBackButtonClick: () => void;
  onNextButtonClick?: () => void;
  formIsSubmitting?: boolean;
  submitOnNextButtonClick?: boolean;
};

export default function AdditionalHardwarePage({
  wizardPageNumberState: [wizardPhase, setWizardPhase],
  onBackButtonClick,
  onNextButtonClick,
  formIsSubmitting = false,
  submitOnNextButtonClick = false,
}: AdditionalHardwarePageProps) {
  const form = Form.useFormInstance<CreateHardwareOrderForm>();
  const items =
    Form.useWatch("pricedHardwareItems", { form, preserve: true }) ?? [];
  return (
    <>
      <WizardPageWrapper
        title="Hardware List"
        disableScroll
        submitOnNext={submitOnNextButtonClick}
        onNextButtonClick={onNextButtonClick}
        onBackButtonClick={onBackButtonClick}
        nextIsLoading={formIsSubmitting}
        disableNext={!items.length}
      >
        <Flex vertical gap="16px">
          <ItemsList
            disableControls={formIsSubmitting}
            title="Hardware"
            items={items}
            form={form}
          />
        </Flex>
      </WizardPageWrapper>
    </>
  );
}

function ItemsList({
  title,
  items,
  form,
  disableControls = false,
}: {
  title: string;
  items: PricedHardwareItemDto[];
  form: FormInstance;
  disableControls?: boolean;
}) {
  return (
    <Flex vertical>
      <Flex gap="16px" align="flex-start">
        <AddItemButton items={items} form={form} disabled={disableControls} />
      </Flex>
      <Flex vertical gap="8px">
        <Row gutter={4}>
          <Col span={12}>
            <Typography style={{ fontWeight: 600 }}>Name</Typography>
          </Col>
          <Col span={4}>
            <Typography style={{ fontWeight: 600 }}>Quantity</Typography>
          </Col>
          <Col span={4}>
            <Typography style={{ fontWeight: 600 }}>Price</Typography>
          </Col>
          <Col span={4}>
            <Typography style={{ fontWeight: 600 }}>Delete</Typography>
          </Col>
        </Row>
        <Divider style={{ margin: "0" }} />
        {items.length ? (
          <>
            {items.map((item) => (
              <Row align="middle" key={item.item} gutter={4}>
                <Col span={12}>
                  <Typography className="line-clamp-1" title={item.description}>
                    {item.description}
                  </Typography>
                </Col>
                <Col span={4}>
                  <Typography>{item.quantity}</Typography>
                </Col>
                <Col span={4}>
                  <Typography>${item.cost.toFixed(2)}</Typography>
                </Col>
                <Col span={4}>
                  <Button
                    type="link"
                    icon={<DeleteOutlined />}
                    disabled={disableControls}
                    onClick={() => {
                      form.setFieldsValue({
                        pricedHardwareItems: items.filter(
                          (_item) => _item.item !== item.item
                        ),
                      });
                    }}
                  />
                </Col>
              </Row>
            ))}
            <Divider style={{ margin: "0" }} />
            <Row>
              <Col span={16}>
                <Typography style={{ fontWeight: 600 }}>Total</Typography>
              </Col>
              <Col span={4}>
                <Typography style={{ fontWeight: 600 }}>
                  $
                  {items
                    .reduce((sum, item) => sum + item.cost * item.quantity, 0)
                    .toFixed(2)}
                </Typography>
              </Col>
              <Col span={4} />
            </Row>
          </>
        ) : (
          <Flex justify="center" align="center" style={{ width: "100%" }}>
            <Typography style={{ padding: "10px" }}>No items added</Typography>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

function AddItemButton({
  items,
  form,
  disabled = false,
}: {
  items: PricedHardwareItemDto[];
  form: FormInstance;
  disabled?: boolean;
}) {
  const [modalPayload, setModalPayload] = useState<
    PricedHardwareItemDto[] | null
  >(null);
  return (
    <>
      <Button
        style={{ padding: "5px 7px", marginBottom: "16px" }}
        type="default"
        icon={<PlusOutlined />}
        disabled={disabled}
        onClick={() => setModalPayload(items)}
      >
        Add/Edit Items
      </Button>
      {modalPayload && (
        <CreateItemsModal
          modalPayloadState={[modalPayload, setModalPayload]}
          onSave={async (_items) => {
            try {
              const requestDto: PricedHardwareItemsRequestDto = {
                items: _items.map((item) => ({
                  item: item.item,
                  quantity: item.quantity,
                })),
              };

              const [response, err] = await getPricedHardwareItems(requestDto);
              if (err || !response) {
                notification.error({
                  message: "Error getting priced items",
                  description: "Failed to get prices for selected items",
                });
                return;
              }

              let updatedItems = [...items];
              response.data.forEach((newItem) => {
                // Remove any existing item with same name
                updatedItems = updatedItems.filter(
                  (item) => item.item !== newItem.item
                );
                // Add the new item
                updatedItems.push(newItem);
              });

              form.setFieldsValue({
                pricedHardwareItems: updatedItems,
              });
              setModalPayload(null);
            } catch (error) {
              notification.error({
                message: "Error saving items",
                description: "Failed to save selected items",
              });
            }
          }}
        />
      )}
    </>
  );
}

type LineItemWithDescription = LineItem & { description: string };

export function CreateItemsModal({
  modalPayloadState: [modalPayload, setModalPayload],
  onSave,
}: {
  modalPayloadState: [
    PricedHardwareItemDto[] | null,
    Dispatch<SetStateAction<PricedHardwareItemDto[] | null>>
  ];
  onSave: (hardwareItems: LineItem[]) => Promise<void>;
}) {
  const [selectedItems, setSelectedItems] = useState<LineItemWithDescription[]>(
    modalPayload?.map((item) => ({
      item: item.item,
      quantity: item.quantity,
      description: item.description,
    })) ?? []
  );
  const [availableItems, setAvailableItems] = useState<HardwareItemDto[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [itemsAreLoading, setItemsAreLoading] = useState<boolean>(false);
  const existingItems = modalPayload ?? [];

  useEffect(() => {
    async function fetchHardwareItems() {
      setItemsAreLoading(true);
      const [response, err] = await getHardwareItems();
      setItemsAreLoading(false);
      if (err || !response) {
        notification.error({
          message: "Error loading hardware items",
          description: "Failed to load available hardware items",
        });
        return;
      }
      setAvailableItems(response.data);
    }
    fetchHardwareItems();
  }, []);

  return (
    <Modal
      open={!!modalPayload}
      footer={null}
      centered
      title={existingItems.length > 0 ? "Edit Line Items" : "Add Line Items"}
      maskClosable={false}
      width={900}
      style={{ maxHeight: "97vh" }}
      keyboard={false}
      onCancel={() => {
        setModalPayload(null);
      }}
    >
      <Row
        style={{
          maxHeight: "calc(99vh - 200px)",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Col span={12}>
          <SelectItemsList
            // This key is used to force a re-render of the component when the available items are set
            key={
              !!availableItems.length ? "available-items" : "no-available-items"
            }
            itemsAreLoading={itemsAreLoading}
            items={availableItems}
            selectedItemsState={[selectedItems, setSelectedItems]}
          />
        </Col>
        <Col span={12}>
          <ItemsSelectedList
            existingItems={existingItems}
            selectedItemState={[selectedItems, setSelectedItems]}
          />
        </Col>
      </Row>

      <ModalSaveCancelButtons
        disableSave={!selectedItems.length || isSaving}
        saveButtonText={
          existingItems.length > 0 ? "Update Items" : "Add Item(s)"
        }
        onSave={async () => {
          if (!!selectedItems.length) {
            setIsSaving(true);
            try {
              await onSave(selectedItems);
            } finally {
              setIsSaving(false);
            }
          }
        }}
        onCancel={() => {
          setModalPayload(null);
        }}
      />
    </Modal>
  );
}

const inputClass = css`
  .ant-input-number-affix-wrapper {
    padding-left: 0;
    border-radius: 5px;
  }
  .ant-input-number-input {
    height: 20px;
  }
`;

function ItemsSelectedList({
  selectedItemState: [selectedItems, setSelectedItems],
  existingItems,
}: {
  selectedItemState: [
    LineItemWithDescription[],
    Dispatch<SetStateAction<LineItemWithDescription[]>>
  ];
  existingItems: PricedHardwareItemDto[];
}) {
  if (!selectedItems.length) {
    return (
      <Flex justify="center" align="center" style={{ width: "100%" }}>
        <Typography style={{ padding: "10px" }}>No items selected</Typography>
      </Flex>
    );
  }
  return (
    <Flex vertical gap="4px">
      <Typography.Title level={5} style={{ paddingLeft: "16px" }}>
        {existingItems.length > 0 ? "Selected Items" : "Items being added"}
      </Typography.Title>
      <List
        style={{
          overflowY: "auto",
          width: "100%",
          paddingLeft: "16px",
          maxHeight: "calc(97vh - 200px)",
        }}
      >
        {selectedItems.map((item) => (
          <List.Item
            className="fade-in"
            style={{ width: "100%", padding: "7px 0" }}
          >
            <Flex
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
              gap="8px"
            >
              <Typography className="line-clamp-1" title={item.description}>
                {item.description}
              </Typography>
              <InputNumber
                prefix="x"
                style={{ maxWidth: "75px", borderRadius: "6px", flexShrink: 0 }}
                className={inputClass}
                value={item.quantity}
                onChange={(value) => {
                  if (value) {
                    item.quantity = value;
                    setSelectedItems([...selectedItems]);
                  }
                }}
              />
            </Flex>
          </List.Item>
        ))}
      </List>
    </Flex>
  );
}

function SelectItemsList({
  items,
  itemsAreLoading,
  selectedItemsState: [selectedItems, setSelectedItems],
}: {
  items: HardwareItemDto[];
  itemsAreLoading: boolean;
  selectedItemsState: [
    LineItemWithDescription[],
    React.Dispatch<React.SetStateAction<LineItemWithDescription[]>>
  ];
}) {
  const categories = Array.from(
    new Set(
      items
        .filter((item) => !!item.category)
        .sort((a, b) => a.category.localeCompare(b.category))
        .map((item) => item.category)
    )
  );
  const [category, setCategory] = useState<string | null>(
    !!categories?.[0] ? categories[0] : null
  );

  const filteredItems = items.filter(
    (item) => !!category && item.category === category
  );

  return (
    <Flex vertical gap="16px" style={{ borderRight: "1px solid #CCCCCC" }}>
      <Flex vertical gap="4px">
        <Typography.Title style={{ fontSize: "12px", marginBottom: "0" }}>
          Item Category
        </Typography.Title>
        <Select
          style={{ marginRight: "16px", borderRadius: "8px" }}
          options={categories.map((category) => ({
            label: category,
            value: category,
          }))}
          value={category}
          onChange={(value) => {
            setCategory(value);
          }}
        />
      </Flex>
      <Flex vertical>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Select Items
        </Typography.Title>
        <List
          style={{
            overflowY: "auto",
            width: "100%",
            paddingRight: "16px",
            maxHeight: "calc(97vh - 200px)",
          }}
        >
          {itemsAreLoading ? (
            <>
              {Array.from({ length: 4 }).map((_, index) => (
                <List.Item key={index} style={{ width: "100%" }}>
                  <Skeleton width="100%" height="20px" key={index} />
                </List.Item>
              ))}
            </>
          ) : filteredItems.length ? (
            <>
              {filteredItems.map((item) => (
                <SelectableItem
                  key={item.item}
                  selectedItemsState={[selectedItems, setSelectedItems]}
                  item={item}
                />
              ))}
            </>
          ) : null}
        </List>
      </Flex>
    </Flex>
  );
}

const lightBackgroundColor = "#eaeaea";
const lightSelectableItemClass = css`
  &:hover {
    background-color: ${lightBackgroundColor};
    border-radius: 8px;
    transition: background-color 0.3s ease;
  }
`;

const darkBackgroundColor = "#5b6475";
const darkSelectableItemClass = css`
  &:hover {
    background-color: ${darkBackgroundColor};
    border-radius: 8px;
    transition: background-color 0.3s ease;
  }
`;

function SelectableItem({
  item,
  selectedItemsState: [selectedItems, setSelectedItems],
}: {
  item: HardwareItemDto;
  selectedItemsState: [
    LineItemWithDescription[],
    React.Dispatch<React.SetStateAction<LineItemWithDescription[]>>
  ];
}) {
  const isSelected = selectedItems.some(
    (selectedItem) => selectedItem.item === item.item
  );

  function handleCheckboxChange(isChecked: boolean) {
    if (isChecked) {
      setSelectedItems([
        ...selectedItems,
        { item: item.item, quantity: 1, description: item.description },
      ]);
    } else {
      setSelectedItems(
        selectedItems.filter((selectedItem) => selectedItem.item !== item.item)
      );
    }
  }

  const darkModeEnabled = useIsDarkModeEnabled();
  return (
    <List.Item
      className={
        darkModeEnabled ? darkSelectableItemClass : lightSelectableItemClass
      }
      onClick={() => {
        handleCheckboxChange(!isSelected);
      }}
      style={{
        width: "100%",
        padding: "8px",
        borderRadius: "8px",
        cursor: "pointer",
        ...(isSelected
          ? {
              backgroundColor: darkModeEnabled
                ? darkBackgroundColor
                : lightBackgroundColor,
            }
          : {}),
      }}
    >
      <Flex gap="8px" style={{ width: "100%" }}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            if (e.target.checked) {
              handleCheckboxChange(true);
            } else {
              handleCheckboxChange(false);
            }
          }}
        />
        <Typography className="line-clamp-1" title={item.description}>
          {item.description}
        </Typography>
      </Flex>
    </List.Item>
  );
}
